import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from './ui/dialog';
import {
  Plus, Trash2, GripVertical, Settings, Copy, Eye, EyeOff,
  Type, Hash, List, CheckSquare, AlignLeft, Calendar, Mail, Phone
} from 'lucide-react';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { value: 'text', label: 'Texto', icon: Type },
  { value: 'number', label: 'Número', icon: Hash },
  { value: 'select', label: 'Lista Seleção', icon: List },
  { value: 'checkbox', label: 'Caixa Verificação', icon: CheckSquare },
  { value: 'textarea', label: 'Texto Longo', icon: AlignLeft },
  { value: 'date', label: 'Data', icon: Calendar },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Telefone', icon: Phone },
];

const SECOES_PADRAO = [
  'Dados Adicionais',
  'Informação Financeira',
  'Recursos Humanos',
  'Infraestrutura',
  'Serviços',
  'Outros'
];

export default function FormBuilder({ campos = [], onChange, readOnly = false }) {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [opcaoTemp, setOpcaoTemp] = useState('');

  const addField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      nome: `campo_${campos.length + 1}`,
      tipo: 'text',
      label: `Novo Campo ${campos.length + 1}`,
      placeholder: '',
      obrigatorio: false,
      opcoes: [],
      valor_padrao: '',
      validacao: '',
      min_valor: null,
      max_valor: null,
      ordem: campos.length,
      secao: 'Dados Adicionais',
      ajuda: ''
    };
    onChange([...campos, newField]);
    setEditingField(newField);
    setEditModalOpen(true);
  };

  const removeField = (fieldId) => {
    onChange(campos.filter(f => f.id !== fieldId));
    toast.success('Campo removido');
  };

  const duplicateField = (field) => {
    const newField = {
      ...field,
      id: `field_${Date.now()}`,
      nome: `${field.nome}_copia`,
      label: `${field.label} (Cópia)`,
      ordem: campos.length
    };
    onChange([...campos, newField]);
    toast.success('Campo duplicado');
  };

  const openEditModal = (field) => {
    setEditingField({ ...field });
    setEditModalOpen(true);
  };

  const saveField = () => {
    if (!editingField.label.trim()) {
      toast.error('O campo precisa de um label');
      return;
    }

    const updatedCampos = campos.map(f =>
      f.id === editingField.id ? editingField : f
    );

    // If it's a new field (not in list yet)
    if (!campos.find(f => f.id === editingField.id)) {
      onChange([...campos, editingField]);
    } else {
      onChange(updatedCampos);
    }

    setEditModalOpen(false);
    setEditingField(null);
    toast.success('Campo guardado');
  };

  const addOpcao = () => {
    if (!opcaoTemp.trim()) return;
    setEditingField(prev => ({
      ...prev,
      opcoes: [...(prev.opcoes || []), opcaoTemp.trim()]
    }));
    setOpcaoTemp('');
  };

  const removeOpcao = (idx) => {
    setEditingField(prev => ({
      ...prev,
      opcoes: prev.opcoes.filter((_, i) => i !== idx)
    }));
  };

  const moveField = (fieldId, direction) => {
    const idx = campos.findIndex(f => f.id === fieldId);
    if (idx === -1) return;
    
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= campos.length) return;

    const newCampos = [...campos];
    [newCampos[idx], newCampos[newIdx]] = [newCampos[newIdx], newCampos[idx]];
    newCampos.forEach((c, i) => c.ordem = i);
    onChange(newCampos);
  };

  // Group fields by section
  const camposPorSecao = campos.reduce((acc, campo) => {
    const secao = campo.secao || 'Dados Adicionais';
    if (!acc[secao]) acc[secao] = [];
    acc[secao].push(campo);
    return acc;
  }, {});

  const getFieldIcon = (tipo) => {
    const fieldType = FIELD_TYPES.find(t => t.value === tipo);
    return fieldType ? fieldType.icon : Type;
  };

  // Preview render for a field
  const renderFieldPreview = (field) => {
    switch (field.tipo) {
      case 'text':
      case 'email':
      case 'phone':
        return <Input placeholder={field.placeholder || field.label} disabled />;
      case 'number':
        return <Input type="number" placeholder={field.placeholder || '0'} disabled />;
      case 'textarea':
        return <Textarea placeholder={field.placeholder || field.label} disabled rows={3} />;
      case 'date':
        return <Input type="date" disabled />;
      case 'select':
        return (
          <Select disabled>
            <SelectTrigger>
              <SelectValue placeholder={field.placeholder || 'Selecione...'} />
            </SelectTrigger>
          </Select>
        );
      case 'checkbox':
        return (
          <div className="space-y-2">
            {(field.opcoes || []).map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <Checkbox disabled />
                <span className="text-sm">{opt}</span>
              </div>
            ))}
          </div>
        );
      default:
        return <Input placeholder={field.label} disabled />;
    }
  };

  if (readOnly) {
    return (
      <div className="space-y-6">
        {Object.entries(camposPorSecao).map(([secao, camposSecao]) => (
          <div key={secao} className="space-y-4">
            <h4 className="font-semibold text-slate-700 border-b pb-2">{secao}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {camposSecao.sort((a, b) => a.ordem - b.ordem).map(campo => (
                <div key={campo.id} className="space-y-1">
                  <Label className="flex items-center gap-1">
                    {campo.label}
                    {campo.obrigatorio && <span className="text-red-500">*</span>}
                  </Label>
                  {renderFieldPreview(campo)}
                  {campo.ajuda && (
                    <p className="text-xs text-slate-500">{campo.ajuda}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="form-builder">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Campos Personalizados</h3>
          <p className="text-sm text-slate-500">
            Adicione campos personalizados ao formulário da campanha
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
            {previewMode ? 'Editar' : 'Pré-visualizar'}
          </Button>
          {!previewMode && (
            <Button
              size="sm"
              onClick={addField}
              className="bg-[#0066CC] hover:bg-[#0052a3]"
              data-testid="add-field-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Adicionar Campo
            </Button>
          )}
        </div>
      </div>

      {/* Preview Mode */}
      {previewMode ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Pré-visualização do Formulário</CardTitle>
          </CardHeader>
          <CardContent>
            {campos.length === 0 ? (
              <p className="text-center text-slate-500 py-8">
                Nenhum campo adicionado ainda
              </p>
            ) : (
              <div className="space-y-6">
                {Object.entries(camposPorSecao).map(([secao, camposSecao]) => (
                  <div key={secao} className="space-y-4">
                    <h4 className="font-semibold text-slate-700 border-b pb-2">{secao}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {camposSecao.sort((a, b) => a.ordem - b.ordem).map(campo => (
                        <div key={campo.id} className="space-y-1">
                          <Label className="flex items-center gap-1">
                            {campo.label}
                            {campo.obrigatorio && <span className="text-red-500">*</span>}
                          </Label>
                          {renderFieldPreview(campo)}
                          {campo.ajuda && (
                            <p className="text-xs text-slate-500">{campo.ajuda}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Edit Mode - Field List */
        <div className="space-y-2">
          {campos.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Type className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">Nenhum campo personalizado</p>
                <p className="text-sm text-slate-400 mt-1">
                  Clique em "Adicionar Campo" para começar
                </p>
              </CardContent>
            </Card>
          ) : (
            campos.sort((a, b) => a.ordem - b.ordem).map((campo, idx) => {
              const FieldIcon = getFieldIcon(campo.tipo);
              return (
                <Card key={campo.id} className="hover:shadow-md transition-shadow" data-testid={`field-${campo.id}`}>
                  <CardContent className="py-3">
                    <div className="flex items-center gap-3">
                      {/* Drag Handle */}
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1"
                          onClick={() => moveField(campo.id, 'up')}
                          disabled={idx === 0}
                        >
                          ▲
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1"
                          onClick={() => moveField(campo.id, 'down')}
                          disabled={idx === campos.length - 1}
                        >
                          ▼
                        </Button>
                      </div>

                      {/* Field Icon */}
                      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                        <FieldIcon className="w-5 h-5 text-slate-600" />
                      </div>

                      {/* Field Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900 truncate">
                            {campo.label}
                          </span>
                          {campo.obrigatorio && (
                            <Badge variant="outline" className="text-red-500 border-red-200 text-xs">
                              Obrigatório
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {FIELD_TYPES.find(t => t.value === campo.tipo)?.label || campo.tipo}
                          </Badge>
                          <span className="text-xs text-slate-400">{campo.secao}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(campo)}
                          data-testid={`edit-field-${campo.id}`}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => duplicateField(campo)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeField(campo.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-field-${campo.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Edit Field Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configurar Campo</DialogTitle>
            <DialogDescription>
              Defina as propriedades do campo personalizado
            </DialogDescription>
          </DialogHeader>

          {editingField && (
            <div className="space-y-4 py-4">
              {/* Label */}
              <div>
                <Label>Label do Campo *</Label>
                <Input
                  value={editingField.label}
                  onChange={(e) => setEditingField(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Ex: Número de Camas"
                  data-testid="field-label-input"
                />
              </div>

              {/* Tipo */}
              <div>
                <Label>Tipo de Campo</Label>
                <Select
                  value={editingField.tipo}
                  onValueChange={(v) => setEditingField(prev => ({ ...prev, tipo: v }))}
                >
                  <SelectTrigger data-testid="field-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Secção */}
              <div>
                <Label>Secção</Label>
                <Select
                  value={editingField.secao}
                  onValueChange={(v) => setEditingField(prev => ({ ...prev, secao: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SECOES_PADRAO.map(secao => (
                      <SelectItem key={secao} value={secao}>{secao}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Placeholder */}
              <div>
                <Label>Placeholder</Label>
                <Input
                  value={editingField.placeholder || ''}
                  onChange={(e) => setEditingField(prev => ({ ...prev, placeholder: e.target.value }))}
                  placeholder="Texto de ajuda dentro do campo"
                />
              </div>

              {/* Opções (para select/checkbox) */}
              {(editingField.tipo === 'select' || editingField.tipo === 'checkbox') && (
                <div>
                  <Label>Opções</Label>
                  <div className="flex gap-2 mb-2">
                    <Input
                      value={opcaoTemp}
                      onChange={(e) => setOpcaoTemp(e.target.value)}
                      placeholder="Nova opção..."
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addOpcao())}
                    />
                    <Button type="button" variant="outline" onClick={addOpcao}>
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {(editingField.opcoes || []).map((opt, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-50 px-2 py-1 rounded">
                        <span className="text-sm">{opt}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOpcao(idx)}
                          className="h-6 w-6 p-0 text-red-500"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Min/Max (para number) */}
              {editingField.tipo === 'number' && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valor Mínimo</Label>
                    <Input
                      type="number"
                      value={editingField.min_valor || ''}
                      onChange={(e) => setEditingField(prev => ({
                        ...prev,
                        min_valor: e.target.value ? parseFloat(e.target.value) : null
                      }))}
                    />
                  </div>
                  <div>
                    <Label>Valor Máximo</Label>
                    <Input
                      type="number"
                      value={editingField.max_valor || ''}
                      onChange={(e) => setEditingField(prev => ({
                        ...prev,
                        max_valor: e.target.value ? parseFloat(e.target.value) : null
                      }))}
                    />
                  </div>
                </div>
              )}

              {/* Ajuda */}
              <div>
                <Label>Texto de Ajuda</Label>
                <Textarea
                  value={editingField.ajuda || ''}
                  onChange={(e) => setEditingField(prev => ({ ...prev, ajuda: e.target.value }))}
                  placeholder="Instruções adicionais para o utilizador..."
                  rows={2}
                />
              </div>

              {/* Obrigatório */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="obrigatorio"
                  checked={editingField.obrigatorio}
                  onCheckedChange={(checked) => setEditingField(prev => ({ ...prev, obrigatorio: checked }))}
                />
                <Label htmlFor="obrigatorio" className="cursor-pointer">
                  Campo Obrigatório
                </Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveField} className="bg-[#0066CC] hover:bg-[#0052a3]">
              Guardar Campo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
